import * as React from 'react'
import { Route, Routes } from 'react-router-dom'

import { FinanceWorkspaceModuleRoutesVisibilityDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { Navigation } from 'modules/core/lib/navigation/context'
import type { NavigationPaths } from 'modules/core/lib/navigation/context'
import { buildModuleRoute } from 'modules/core/lib/react-router'
import { buildNavigation } from 'modules/core/lib/react-router'

const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const FinancialWorkspacePayrollPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/FinanceWorkspacePayroll')
)

const FinancialWorkspaceCompensationPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/FinanceWorkspaceCompensation')
)

const FinancialWorkspaceEmployeeCostsPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/FinancialWorkspaceEmployeeCosts')
)

const ImportPayrollResultModalPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/ImportPayrollResultModal')
)

const ImportFteModalPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/ImportFteModal')
)

const FinancialWorkspaceRootPage = React.lazy(
  () => import('modules/finance/pages/FinancialWorkspacePage/RootPage')
)

const HeadcountPlanningPage = React.lazy(
  () => import('modules/planning/pages/HeadcountPlanningPage')
)

const buildSettingItems = (navigation: Navigation<NavigationPaths>, seeSettings?: boolean) => {
  return seeSettings ? navigation.settings.financialWorkspace.fullPath() : undefined
}

export const financeWorkspaceRoutes = buildNavigation((navigation) => {
  return [
    buildModuleRoute({
      path: navigation.finance.financialWorkspace.path,
      element: <FinancialWorkspaceRootPage />,
      handle: {
        module: 'finance-workspace',
        moduleName: () => i18n.t('sidebar.finance_workspace'),
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: FinanceWorkspaceModuleRoutesVisibilityDocument,
          }).map(({ data }) => {
            const canSeeFinancialWorkspace =
              data?.financialWorkspace?._policies?.seeFinancialWorkspace?.can ?? false
            const canSeePayroll = data?.financialWorkspace?._policies?.payrollSee?.can ?? false
            const canSeeCompensations =
              data?.financialWorkspace?._policies?.compensationsSee?.can ?? false
            const payrollSummaryUploadedLastYear =
              data?.financialWorkspace.payrollLandingBannersConnection.edges[0]?.node
                .payrollSummaryUploadedLastYear ?? false
            const canSeeHeadcountPlanning =
              data?.planning?._policies?.featuresHeadcountPlanning?.can ?? false

            return {
              settings: buildSettingItems(navigation, canSeeFinancialWorkspace),
              subNavigation: [
                {
                  id: 'employee-costs',
                  title: i18n.t('finance.financial_workspace.subNavigation.employee_costs.title'),
                  href: navigation.finance.financialWorkspace.employee_costs.fullPath(),
                  isVisible: canSeeFinancialWorkspace,
                },
                {
                  id: 'payroll',
                  title: i18n.t('finance.financial_workspace.subNavigation.payroll_data.title'),
                  href: navigation.finance.financialWorkspace.payroll.fullPath(),
                  isVisible:
                    canSeeFinancialWorkspace &&
                    payrollSummaryUploadedLastYear &&
                    canSeePayroll &&
                    !canSeeCompensations,
                },
                {
                  id: 'compensations',
                  title: i18n.t('finance.financial_workspace.compensations.compensation'),
                  href: navigation.finance.financialWorkspace.compensations.fullPath(),
                  isVisible: canSeeFinancialWorkspace && canSeeCompensations,
                },
                {
                  id: 'headcount-planning',
                  title: i18n.t('planning.headcount_planning.title'),
                  href: navigation.finance.financialWorkspace.headcountPlanning.fullPath(),
                  isVisible: canSeeHeadcountPlanning,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <ReroutingIndex />,
        },
        {
          handle: {
            meta: () => ({
              title: i18n.t('finance.financial_workspace.subNavigation.employee_costs.title'),
            }),
          },
          path: navigation.finance.financialWorkspace.employee_costs.path,
          element: <FinancialWorkspaceEmployeeCostsPage />,
          children: [
            {
              path: navigation.finance.financialWorkspace.employee_costs.import_result.path,
              element: (
                <Routes>
                  <Route path="" element={<ImportPayrollResultModalPage />} />
                </Routes>
              ),
            },
            {
              path: navigation.finance.financialWorkspace.employee_costs.import_fte.path,
              element: (
                <Routes>
                  <Route path="" element={<ImportFteModalPage />} />
                </Routes>
              ),
            },
          ],
        },
        {
          handle: {
            meta: () => ({
              title: i18n.t('finance.financial_workspace.subNavigation.payroll_data.title'),
            }),
          },
          path: navigation.finance.financialWorkspace.payroll.path,
          element: <FinancialWorkspacePayrollPage />,
          children: [
            {
              path: navigation.finance.financialWorkspace.payroll.import_result.path,
              element: <ImportPayrollResultModalPage />,
            },
          ],
        },
        {
          handle: {
            meta: () => ({
              title: i18n.t('finance.financial_workspace.compensations.compensation'),
            }),
          },
          path: navigation.finance.financialWorkspace.compensations.path,
          element: <FinancialWorkspaceCompensationPage />,
          children: [
            {
              path: navigation.finance.financialWorkspace.compensations.import_result.path,
              element: (
                <Routes>
                  <Route path="" element={<ImportPayrollResultModalPage />} />
                </Routes>
              ),
            },
          ],
        },
        {
          handle: {
            meta: () => ({
              title: i18n.t('planning.headcount_planning.title'),
            }),
          },
          path: navigation.finance.financialWorkspace.headcountPlanning.path,
          element: <HeadcountPlanningPage />,
        },
      ],
    }),
  ]
})
